import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { AMBASSADOR_SOURCE_LOCATION } from '#src/app/shared/constants';
import { normalize } from '#src/app/shared/utils/string';

import type { PublishedPetitionResponse } from './api/createPublishedPetition';
import { fetchSocialStarterAnglePublishData } from './api/socialStarterAngleSourceLocation';
/**
 * Whether the resolved `source_location` identifies an ambassador petition, independent of the
 * `ambassadorPostCreateFlow` feature config. Used to keep ambassador petitions off the
 * starter_experience_start_strong_after_sap_v3 redirect, regardless of whether the
 * ambassador-onboarding redirect itself is currently enabled — an ambassador petition shouldn't be
 * eligible for a *different* post-publish experience just because its own dedicated flow is off.
 *
 * Branches on the resolved `source_location` rather than the petition's `isAmbassadorPetition`
 * field: staffing is applied asynchronously, so that field can still read false here — and that
 * false would then be cached for 30 minutes.
 */
export function isAmbassadorSourceLocation({
	resolvedSourceLocation,
}: {
	resolvedSourceLocation: string | undefined;
}): boolean {
	return resolvedSourceLocation === AMBASSADOR_SOURCE_LOCATION;
}

/**
 * Whether a publish redirects to ambassador-onboarding — gated on `isAmbassadorPostCreateFlowEnabled`,
 * so with that config off an ambassador petition takes the standard onboarding flow. Only used by
 * `resolvePostPublishPath`; the start-strong resolution deliberately uses the flag-independent
 * `isAmbassadorSourceLocation` instead.
 */
function isAmbassadorPetitionRedirect({
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
}: {
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
}): boolean {
	return isAmbassadorPostCreateFlowEnabled && isAmbassadorSourceLocation({ resolvedSourceLocation });
}

/**
 * Checks whether an AI draft has been edited by comparing the normalized base and current values.
 * This information is passed in the activate_petition tracking event.
 */
/**
 * Attaching DMI-inferred decision makers is gated on the sap_decision_maker_experiment step
 * experiment being live (its FCM is on), not on the variant: every group attaches, and only the
 * variant shows the decision maker UI. Keying off `fallback` keeps the attach and the UI on the same
 * switch - when the experiment falls back to control (FCM off, not registered, fetch error), there
 * is no decision maker UI and nothing is attached.
 */
export function isDecisionMakerAttachEnabled(decisionMakerExperiment: { fallback?: boolean | null }): boolean {
	return !decisionMakerExperiment.fallback;
}

export function compareNormalizedStrings(base: string | undefined | null, value: string): boolean | null {
	if (base === undefined || base === null) return null;

	return normalize(base) !== normalize(value);
}

/**
 * Resolves the Social Starter data to publish with. The draft-angle record is the source of truth
 * for both staffing attribution and the matched decision maker. Non-Social-Starter drafts preserve
 * the URL attribution and have no decision maker.
 */
export async function resolvePublishContext({
	angleToken,
	urlSourceLocation,
	utilityContext,
}: {
	angleToken: string | null | undefined;
	urlSourceLocation: string | undefined;
	utilityContext: UtilityContext;
}): Promise<Readonly<{ sourceLocation: string | undefined; decisionMakerId: string | undefined }>> {
	if (!angleToken) return { sourceLocation: urlSourceLocation, decisionMakerId: undefined };

	const publishData = await fetchSocialStarterAnglePublishData({ token: angleToken, utilityContext });
	// A blank stamped code shouldn't happen, but guard it the same as a missing one — the petition
	// service validates source_location against a fixed set, so an empty string would 400 rather
	// than silently falling back to the URL value.
	return {
		sourceLocation: publishData?.sourceLocation || urlSourceLocation,
		decisionMakerId: publishData?.decisionMakerId ?? undefined,
	};
}

type PostPublishDestination = 'ambassador' | 'start_strong' | 'starter_onboarding';

/**
 * Where a publish redirects to — the `destination` on `StartStrongRedirectResolved`, and the branch
 * `resolvePostPublishPath` builds its path from, so the two can't disagree.
 *
 * The ambassador check takes precedence over the start-strong redirect: an ambassador petition
 * always goes to ambassador-onboarding regardless of `isStartStrongAfterSapEnabled`.
 *
 * `isStartStrongAfterSapEnabled` must already reflect whether the viewer is actually treated (or
 * overridden) in the experiment — see the caller's exposure resolution in `usePrePublish/index.ts`.
 * This function only applies the ambassador/redirect precedence; it doesn't re-derive eligibility.
 */
export function resolvePostPublishDestination({
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
	isStartStrongAfterSapEnabled,
}: {
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
	isStartStrongAfterSapEnabled: boolean;
}): PostPublishDestination {
	if (isAmbassadorPetitionRedirect({ resolvedSourceLocation, isAmbassadorPostCreateFlowEnabled })) return 'ambassador';
	if (isStartStrongAfterSapEnabled) return 'start_strong';
	return 'starter_onboarding';
}

const POST_PUBLISH_PATH_SUFFIXES: Readonly<Record<PostPublishDestination, string>> = {
	ambassador: 'ambassador-onboarding',
	start_strong: 'start-strong/intro',
	starter_onboarding: 'starter-onboarding/welcome',
};

export function resolvePostPublishPath({
	petition,
	destination,
}: {
	petition: Pick<PublishedPetitionResponse, 'id' | 'slug'>;
	destination: PostPublishDestination;
}): string {
	return `/p/${petition.slug ?? petition.id}/${POST_PUBLISH_PATH_SUFFIXES[destination]}`;
}
